import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AddPickUpPointModal from '../modals/AddPickUpPointModal';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { PickUpPoint } from '../types';
import { useAuth } from '../hooks/useAuth';

const PickUpPoints = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pickUpPoints, setPickUpPoints] = useState<PickUpPoint[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPickUpPoint, setSelectedPickUpPoint] = useState<PickUpPoint | null>(null);
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const isAdmin = user?.role === 'admin';
  const actionMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
        setOpenActionMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPickUpPoints = async (search: string = '', page: number = 1) => {
    setIsLoading(true);
    try {
      const query = new URLSearchParams();
      if (search) query.append('search', search);
      query.append('page', String(page));
      const url = `/pick_up_points?${query.toString()}`;

      const res = await fetchWithAuth(url);
      if (!res.ok) throw new Error('Failed to fetch pick-up points');
      const data = await res.json();
      setPickUpPoints(data.pick_up_points);
      setTotalPages(data.total_pages);
      setCurrentPage(data.current_page);
      setError(null);
    } catch (err) {
      setError('Something went wrong while fetching pick-up points.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initialPage = parseInt(searchParams.get('page') || '1', 10);
    const page = isNaN(initialPage) || initialPage < 1 ? 1 : initialPage;
    fetchPickUpPoints('', page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (point: PickUpPoint) => {
    setSelectedPickUpPoint(point);
    setIsModalOpen(true);
  };

  return (
    <div className="px-4 md:px-6 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Pick-Up Points</h1>
        {isAdmin && (
          <button
            onClick={() => {
              setSelectedPickUpPoint(null);
              setIsModalOpen(true);
            }}
            className="w-full sm:w-auto bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800"
          >
            Add Pick-Up Point
          </button>
        )}
      </div>
      {error && <div className="text-red-600 font-medium mb-4">{error}</div>}

      <div className="mb-4">
        <div className="flex flex-col md:flex-row items-stretch md:items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Search</label>
            <input
              type="text"
              placeholder="Search by Name or Code"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearchParams(prev => { prev.set('page', '1'); return prev; });
                  fetchPickUpPoints(searchTerm, 1);
                }
              }}
              className="border px-4 py-2 rounded-md w-full md:w-80"
            />
          </div>
          <button
            onClick={() => {
              setSearchParams(prev => { prev.set('page', '1'); return prev; });
              fetchPickUpPoints(searchTerm, 1);
            }}
            disabled={!searchTerm}
            className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Search
          </button>
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm('');
                setSearchParams(prev => { prev.set('page', '1'); return prev; });
                fetchPickUpPoints('', 1);
              }}
              className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md font-semibold hover:bg-gray-400"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="w-full h-1 bg-gray-200 rounded overflow-hidden mb-2">
          <div className="h-full bg-blue-500 animate-pulse" style={{ width: '100%' }}></div>
        </div>
      )}
      <div className="overflow-x-auto min-h-[200px] pb-12">
        <table className="min-w-full bg-white border border-gray-200 rounded-md shadow-sm">
          <thead>
            <tr className="bg-gray-100 text-left text-sm font-medium text-gray-700">
              <th className="px-2 md:px-4 py-1">ID</th>
              <th className="px-2 md:px-4 py-2">Name</th>
              <th className="px-2 md:px-4 py-1">Code</th>
              <th className="hidden md:table-cell px-4 py-1">Collection Fee</th>
              <th className="hidden md:table-cell px-4 py-1">City</th>
              <th className="hidden md:table-cell px-4 py-1">Default</th>
              <th className="px-2 md:px-4 py-1">Status</th>
              <th className="hidden md:table-cell px-4 py-1">Verified Numbers</th>
              {isAdmin && <th className="px-2 md:px-4 py-1">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {pickUpPoints.map((point) => (
              <tr
                key={point.id}
                className={`border-t border-gray-100 text-sm text-gray-800 hover:bg-yellow-100 ${
                  openActionMenuId === point.id ? 'bg-yellow-100' : ''
                }`}
              >
                <td className="px-2 md:px-4 py-1">{point.id}</td>
                <td className="px-2 md:px-4 py-2">
                  <a href={`/pick-up-points/${point.id}`} className="text-blue-600 hover:text-blue-800">
                    {point.name}
                  </a>
                </td>
                <td className="px-2 md:px-4 py-1">{point.code}</td>
                <td className="hidden md:table-cell px-4 py-1">
                  {point.collection_fee ? `E${Number(point.collection_fee).toFixed(2)}` : <span className="text-gray-300">-</span>}
                </td>
                <td className="hidden md:table-cell px-4 py-1">
                  {point.city || <span className="text-gray-300">-</span>}
                </td>
                <td className="hidden md:table-cell px-4 py-1">
                  <span className={`inline-block w-3 h-3 rounded-full ${point.default ? 'bg-green-500' : 'bg-gray-300'}`} />
                </td>
                <td className="px-2 md:px-4 py-1">
                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${point.active ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                    {point.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="hidden md:table-cell px-4 py-1 text-gray-500">
                  {point.verified_numbers && point.verified_numbers.length > 0
                    ? point.verified_numbers.map((vn) => vn.phone_number).join(', ')
                    : <span className="text-gray-300">-</span>}
                </td>
                {isAdmin && (
                  <td className="px-2 md:px-4 py-1">
                    <div ref={openActionMenuId === point.id ? actionMenuRef : undefined} className="relative inline-block text-left">
                      <button
                        onClick={() => setOpenActionMenuId(openActionMenuId === point.id ? null : point.id)}
                        className="p-1 rounded hover:bg-gray-100"
                      >
                        <span className="text-xl">&#x22EF;</span>
                      </button>
                      {openActionMenuId === point.id && (
                        <div className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded shadow-md z-10">
                          <button
                            onClick={() => {
                              handleEdit(point);
                              setOpenActionMenuId(null);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-center items-center space-x-4 mt-4">
          <button
            disabled={currentPage === 1}
            onClick={() => {
              const newPage = currentPage - 1;
              setSearchParams(prev => { prev.set('page', String(newPage)); return prev; });
              fetchPickUpPoints(searchTerm, newPage);
            }}
            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => {
              const newPage = currentPage + 1;
              setSearchParams(prev => { prev.set('page', String(newPage)); return prev; });
              fetchPickUpPoints(searchTerm, newPage);
            }}
            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
      <AddPickUpPointModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPickUpPoint(null);
        }}
        onSuccess={() => {
          setIsModalOpen(false);
          setSelectedPickUpPoint(null);
          fetchPickUpPoints(searchTerm, currentPage);
        }}
        selectedPickUpPoint={selectedPickUpPoint}
      />
    </div>
  );
};

export default PickUpPoints;
